import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

type Pt = { x: number; y: number };

/** Tam ekran şəkil baxıcısı: sürüşdür, yaxınlaşdır (pinch/wheel), bağla. */
export function ImageLightbox({
  images,
  index,
  onIndexChange,
  onClose,
  alt,
}: {
  images: string[];
  index: number;
  onIndexChange: (i: number) => void;
  onClose: () => void;
  alt: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<Pt>({ x: 0, y: 0 });
  const pointers = useRef(new Map<number, Pt>());
  const pinch = useRef<{ dist: number; center: Pt } | null>(null);
  const drag = useRef<{ start: Pt; offset: Pt; moved: boolean } | null>(null);
  const stateRef = useRef({ zoom, offset });
  stateRef.current = { zoom, offset };

  const reset = useCallback(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const go = useCallback(
    (dir: number) => {
      if (images.length < 2) return;
      onIndexChange((index + dir + images.length) % images.length);
      reset();
    },
    [images.length, index, onIndexChange, reset],
  );

  // Kursor nöqtəsini sabit saxlayaraq miqyaslama
  const zoomAt = useCallback((next: number, px: number, py: number) => {
    const { zoom: z, offset: o } = stateRef.current;
    const target = clamp(next, MIN_ZOOM, MAX_ZOOM);
    const k = target / z;
    setZoom(target);
    setOffset(
      target === MIN_ZOOM
        ? { x: 0, y: 0 }
        : { x: px - (px - o.x) * k, y: py - (py - o.y) * k },
    );
  }, []);

  const wheelRef = useRef((e: WheelEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1);
    zoomAt(
      stateRef.current.zoom * Math.exp(-dy * 0.0018),
      e.clientX - rect.left - rect.width / 2,
      e.clientY - rect.top - rect.height / 2,
    );
  });
  wheelRef.current = (e: WheelEvent) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1);
    zoomAt(
      stateRef.current.zoom * Math.exp(-dy * 0.0018),
      e.clientX - rect.left - rect.width / 2,
      e.clientY - rect.top - rect.height / 2,
    );
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      wheelRef.current(e);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [go, onClose]);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = {
        dist: Math.hypot(a!.x - b!.x, a!.y - b!.y),
        center: { x: (a!.x + b!.x) / 2, y: (a!.y + b!.y) / 2 },
      };
      drag.current = null;
    } else {
      drag.current = { start: { x: e.clientX, y: e.clientY }, offset, moved: false };
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = (a!.x + b!.x) / 2 - rect.left - rect.width / 2;
      const cy = (a!.y + b!.y) / 2 - rect.top - rect.height / 2;
      zoomAt(stateRef.current.zoom * (dist / pinch.current.dist), cx, cy);
      pinch.current = { dist, center: { x: cx, y: cy } };
      return;
    }

    if (drag.current) {
      const dx = e.clientX - drag.current.start.x;
      const dy = e.clientY - drag.current.start.y;
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) drag.current.moved = true;
      if (zoom > 1) setOffset({ x: drag.current.offset.x + dx, y: drag.current.offset.y + dy });
    }
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const d = drag.current;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (d && zoom === 1) {
      const dx = e.clientX - d.start.x;
      if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
    }
    drag.current = null;
  };

  const [lastTap, setLastTap] = useState(0);
  const onImageClick = (e: React.MouseEvent) => {
    const now = Date.now();
    if (now - lastTap < 300) {
      const el = containerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        zoomAt(
          zoom > 1 ? 1 : 2.5,
          e.clientX - rect.left - rect.width / 2,
          e.clientY - rect.top - rect.height / 2,
        );
      }
    }
    setLastTap(now);
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[999] flex flex-col bg-black/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Şəkil baxışı"
    >
      <div className="flex items-center justify-between p-3 text-white">
        <span className="rounded-full bg-white/10 px-3 py-1 text-sm font-semibold">
          {index + 1} / {images.length}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            aria-label="Uzaqlaşdır"
            onClick={() => zoomAt(zoom - 0.6, 0, 0)}
            className="grid size-10 place-items-center rounded-full bg-white/10 hover:bg-white/20"
          >
            <ZoomOut className="size-5" />
          </button>
          <button
            type="button"
            aria-label="Yaxınlaşdır"
            onClick={() => zoomAt(zoom + 0.6, 0, 0)}
            className="grid size-10 place-items-center rounded-full bg-white/10 hover:bg-white/20"
          >
            <ZoomIn className="size-5" />
          </button>
          <button
            type="button"
            aria-label="Bağla"
            onClick={onClose}
            className="grid size-10 place-items-center rounded-full bg-white/10 hover:bg-white/20"
          >
            <X className="size-5" />
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="relative flex-1 touch-none select-none overflow-hidden"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className="absolute inset-0 grid place-items-center">
          <img
            src={images[index]}
            alt={alt}
            draggable={false}
            onClick={onImageClick}
            className="max-h-full max-w-full object-contain"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
              cursor: zoom > 1 ? "grab" : "zoom-in",
            }}
          />
        </div>

        {images.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Əvvəlki şəkil"
              onClick={() => go(-1)}
              className="absolute left-2 top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white hover:bg-white/25"
            >
              <ChevronLeft className="size-6" />
            </button>
            <button
              type="button"
              aria-label="Növbəti şəkil"
              onClick={() => go(1)}
              className="absolute right-2 top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white hover:bg-white/25"
            >
              <ChevronRight className="size-6" />
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto p-3">
          {images.map((src, i) => (
            <button
              key={src + i}
              type="button"
              aria-label={`Şəkil ${i + 1}`}
              onClick={() => {
                onIndexChange(i);
                reset();
              }}
              className={`shrink-0 overflow-hidden rounded-xl border-2 ${
                i === index ? "border-primary" : "border-transparent opacity-60"
              }`}
            >
              <img src={src} alt="" className="h-16 w-20 object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}
